import { Injectable, signal, computed, Signal, inject } from '@angular/core';
import { AuthService } from '@brightside/core/auth';

export enum MenuKey {
  MAIN = 'main',
}

export interface MenuItem {
  id: string;
  menuKey: MenuKey;
  label: string;
  icon?: string;
  route?: string;
  parentId?: string;
  groups?: string | string[];
  order?: number;
  children?: MenuItem[];
}

@Injectable({ providedIn: 'root' })
export class MenuStore {
  private _items = signal<MenuItem[]>([]);
  readonly items = computed(() => this._items());
  private _auth = inject(AuthService);

  register(item: MenuItem): void {
    this._items.update(current => {
      const merged = [...current, item];
      return merged.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });
  }

  menu(menuKey: MenuKey): Signal<MenuItem[]> {
    return computed(() => {
      const all = this.items().filter(i => i.menuKey === menuKey && this.hasAccess(i));

      const rootItems = all.filter(i => !i.parentId);
      const childMap = new Map<string, MenuItem[]>();

      for (const item of all) {
        if (item.parentId) {
          const children = childMap.get(item.parentId) ?? [];
          children.push(item);
          childMap.set(item.parentId, children);
        }
      }

      return rootItems
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(item => ({
          ...item,
          children: (childMap.get(item.id) ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        }));
    });
  }

  private hasAccess(item: MenuItem): boolean {
    if (!item.groups || (Array.isArray(item.groups) && item.groups.length === 0)) {
      return true;
    }
    const required = Array.isArray(item.groups) ? item.groups : [item.groups];
    return required.some(group => this._auth.isInGroup(group));
  }
}
